import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, Download, Loader2, ExternalLink, Monitor, Smartphone, Tablet, Check, X } from "lucide-react";
import { useReportDownloads, fetchAllReportDownloads } from "@/hooks/superadmin/useReportDownloads";
import PaginationControls from "@/components/designer-grid/PaginationControls";
import { useToast } from "@/hooks/use-toast";
import { exportReportDownloadsToCSV, downloadCSV } from "@/utils/exportReportDownloads";

const ReportDownloadsTable = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [marketingConsentFilter, setMarketingConsentFilter] = useState<"all" | "yes" | "no">("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [isExporting, setIsExporting] = useState(false);
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const dragStateRef = useRef({
    isDragging: false,
    startX: 0,
    scrollLeft: 0,
  });
  const [isDragScrollable, setIsDragScrollable] = useState(false);

  const { toast } = useToast();

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to first page when search changes
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [marketingConsentFilter]);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (!dragStateRef.current.isDragging) return;
      const container = tableScrollRef.current;
      if (!container) return;

      const deltaX = event.clientX - dragStateRef.current.startX;
      container.scrollLeft = dragStateRef.current.scrollLeft - deltaX;
      event.preventDefault();
    };

    const handleMouseUp = () => {
      if (!dragStateRef.current.isDragging) return;
      dragStateRef.current.isDragging = false;
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const handleTableMouseDown = (
    event: React.MouseEvent<HTMLDivElement>
  ) => {
    if (event.button !== 0) return;
    const target = event.target as HTMLElement;
    if (target.closest("button, input, select, textarea, a, [role='button']")) {
      return;
    }
    const container = tableScrollRef.current;
    if (!container) return;

    dragStateRef.current.isDragging = true;
    dragStateRef.current.startX = event.clientX;
    dragStateRef.current.scrollLeft = container.scrollLeft;
    document.body.style.userSelect = "none";
    document.body.style.cursor = "grabbing";
    event.preventDefault();
  };

  // Fetch report downloads
  const {
    data: reportDownloads,
    pagination,
    stats,
    isLoading,
    error,
  } = useReportDownloads({
    page: currentPage,
    pageSize,
    searchTerm: debouncedSearchTerm,
    marketingConsentFilter,
  });

  useEffect(() => {
    const container = tableScrollRef.current;
    if (!container) return;

    const updateScrollable = () => {
      setIsDragScrollable(container.scrollWidth > container.clientWidth);
    };

    updateScrollable();
    window.addEventListener("resize", updateScrollable);
    return () => window.removeEventListener("resize", updateScrollable);
  }, [reportDownloads]);

  // Use stats if available (when no filters), otherwise calculate from current page
  const totalDownloads = stats?.totalDownloads ?? reportDownloads?.length ?? 0;
  const marketingOptIns = stats?.marketingOptIns ?? reportDownloads?.filter((d) => d.marketing_consent).length ?? 0;

  // Format date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Format consent badge - compact icon style
  const getConsentBadge = (consent: boolean | null, label: string) => {
    if (consent) {
      return (
        <Badge className="bg-green-100 text-green-800 border-green-200 text-xs inline-flex items-center gap-1 px-1.5 py-0.5">
          <Check className="h-3 w-3" />
          {label}
        </Badge>
      );
    } else {
      return (
        <Badge className="bg-gray-100 text-gray-500 border-gray-200 text-xs inline-flex items-center gap-1 px-1.5 py-0.5">
          <X className="h-3 w-3" />
          {label}
        </Badge>
      );
    }
  };

  // Get device type icon and badge
  const getDeviceTypeBadge = (deviceType: string | null) => {
    const type = deviceType?.toLowerCase() || "desktop";
    switch (type) {
      case "mobile":
        return (
          <Badge className="bg-purple-100 text-purple-800 border-purple-200 inline-flex items-center gap-1">
            <Smartphone className="h-3 w-3" />
            Mobile
          </Badge>
        );
      case "tablet":
        return (
          <Badge className="bg-orange-100 text-orange-800 border-orange-200 inline-flex items-center gap-1">
            <Tablet className="h-3 w-3" />
            Tablet
          </Badge>
        );
      default:
        return (
          <Badge className="bg-blue-100 text-blue-800 border-blue-200 inline-flex items-center gap-1">
            <Monitor className="h-3 w-3" />
            Desktop
          </Badge>
        );
    }
  };

  // Format browser info
  const formatBrowser = (browserName: string | null, browserVersion: string | null) => {
    if (!browserName) return "-";
    return browserVersion ? `${browserName} ${browserVersion}` : browserName;
  };

  // Format OS info
  const formatOS = (osName: string | null, osVersion: string | null) => {
    if (!osName) return "-";
    return osVersion ? `${osName} ${osVersion}` : osName;
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      // Fetch all filtered report downloads
      const allDownloads = await fetchAllReportDownloads({
        searchTerm: debouncedSearchTerm,
        marketingConsentFilter,
      });

      if (allDownloads.length === 0) {
        toast({
          title: "No data to export",
          description: "There are no report downloads matching the current filters.",
          variant: "destructive",
        });
        return;
      }

      // Convert to CSV
      const csvContent = exportReportDownloadsToCSV(allDownloads);

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split("T")[0];
      const filename = `report-downloads-${timestamp}.csv`;

      // Download the CSV file
      downloadCSV(csvContent, filename);

      toast({
        title: "Export successful",
        description: `Exported ${allDownloads.length} download${allDownloads.length !== 1 ? "s" : ""} to CSV.`,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to export report downloads";

      toast({
        title: "Export failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="flex flex-col h-full space-y-4">
      {/* Header with stats and actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 flex-shrink-0">
        <div className="flex flex-wrap items-center gap-4">
          <Badge className="bg-blue-100 text-blue-800 border-blue-200 px-3 py-1.5 text-sm font-medium">
            {totalDownloads} Total Downloads
          </Badge>
          <Badge className="bg-green-100 text-green-800 border-green-200 px-3 py-1.5 text-sm font-medium">
            {marketingOptIns} Marketing Opt-ins
          </Badge>
        </div>
        <Button
          variant="default"
          disabled={isExporting}
          onClick={handleExport}
        >
          {isExporting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Exporting...
            </>
          ) : (
            <>
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </>
          )}
        </Button>
      </div>

      {/* Search and filter */}
      <div className="flex flex-col sm:flex-row gap-4 flex-shrink-0">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, or LinkedIn..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select
          value={marketingConsentFilter}
          onValueChange={(value) => setMarketingConsentFilter(value as "all" | "yes" | "no")}
        >
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder="Marketing Consent" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="yes">Marketing: Yes</SelectItem>
            <SelectItem value="no">Marketing: No</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="rounded-md border overflow-hidden flex-1 min-h-0 flex flex-col">
        <Table
          wrapperRef={tableScrollRef}
          wrapperProps={{
            className: `flex-1 min-h-0 ${
              isDragScrollable ? "cursor-grab active:cursor-grabbing" : ""
            }`,
            onMouseDown: handleTableMouseDown,
          }}
        >
          <TableHeader className="sticky top-0 bg-white z-10 shadow-sm border-b">
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>LinkedIn</TableHead>
              <TableHead>Version</TableHead>
              <TableHead>Marketing</TableHead>
              <TableHead>Device</TableHead>
              <TableHead>Browser</TableHead>
              <TableHead>OS</TableHead>
              <TableHead>IP Address</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                  Loading report downloads...
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={11} className="text-center py-8 text-red-600">
                  Error loading report downloads: {error.message}
                </TableCell>
              </TableRow>
            ) : !reportDownloads || reportDownloads.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                  No report downloads found
                </TableCell>
              </TableRow>
            ) : (
              reportDownloads.map((download) => (
                <TableRow key={download.id}>
                  <TableCell className="font-medium">{download.name}</TableCell>
                  <TableCell>
                    <a
                      href={`mailto:${download.email}`}
                      className="text-blue-600 hover:underline"
                    >
                      {download.email}
                    </a>
                  </TableCell>
                  <TableCell>
                    <a
                      href={download.linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                    >
                      <span className="max-w-[200px] truncate">
                        {download.linkedin_url.replace("https://", "").replace("www.", "")}
                      </span>
                      <ExternalLink className="h-3 w-3 flex-shrink-0" />
                    </a>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {download.report_version_label || "-"}
                  </TableCell>
                  <TableCell>
                    {getConsentBadge(download.marketing_consent, "Marketing")}
                  </TableCell>
                  <TableCell>{getDeviceTypeBadge(download.device_type)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatBrowser(download.browser_name, download.browser_version)}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatOS(download.os_name, download.os_version)}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground font-mono">
                    {download.ip_address || "-"}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">
                      {download.source || "homepage_banner"}
                    </Badge>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {formatDate(download.created_at)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <PaginationControls
          pagination={pagination}
          onPageChange={setCurrentPage}
        />
      )}
    </div>
  );
};

export default ReportDownloadsTable;
